import { buildRequestData, checkAndGetOriginResp } from '@/utils/cipher'
import { useUserStore } from '@/stores/user'
import { getErrorMessage } from '@/utils/errorMessage'
import i18n from '@/i18n'

export interface ApiError {
  message: string
  status: number
  code?: number
}

const BASE_URL = '/api/v1'
const DEFAULT_TIMEOUT = 30000
const CIPHER_ENABLED = import.meta.env.VITE_ENABLE_CIPHER === 'true'

// 需要强制登出的业务错误码
const AUTH_ERROR_CODES = new Set([
  10101, // CodeUserNotFound — token 对应的用户不存在
  41001, // CodeTokenMissing
  41002, // CodeTokenInvalid
  41003, // CodeTokenExpired
  41004, // CodeSessionNotFound
  41005, // CodeSessionExpired
])

class ApiClient {
  private baseURL: string
  private token: string | null = null

  constructor(baseURL: string = BASE_URL) {
    this.baseURL = baseURL
  }

  setAuthToken(token: string | null) {
    this.token = token
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT)

    const headers: Record<string, string> = {}
    headers['Accept-Language'] = i18n.language
    // token 以 store 为准：onRehydrateStorage 的回调在 rehydrate 之后异步触发，
    // 组件的首个 useEffect 可能先于它执行 —— 那次请求会漏带 token 并拿到 41003，
    // 进而把刚恢复的登录态清掉。这里兜底读 store，消除对"推送时序"的依赖。
    const token = this.token || useUserStore.getState().token
    if (token) {
      headers['X-CT'] = token
    }

    let body: BodyInit | undefined

    if (CIPHER_ENABLED) {
      // 加密模式：JSON → 加密二进制
      const jsonStr = data !== undefined ? JSON.stringify(data) : '{}'
      const encrypted = buildRequestData(jsonStr)
      headers['Content-Type'] = 'application/octet-stream'
      body = encrypted.buffer as ArrayBuffer
    } else {
      // 明文模式
      headers['Content-Type'] = 'application/json'
      body = data !== undefined ? JSON.stringify(data) : undefined
    }

    try {
      const response = await fetch(this.baseURL + endpoint, {
        method: 'POST',
        headers,
        body,
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (CIPHER_ENABLED) {
        return this.handleCipherResponse<T>(response)
      }

      return this.handlePlainResponse<T>(response)
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof Error && error.name === 'AbortError') {
        throw { message: i18n.t('error.timeout'), status: 408 } as ApiError
      }
      throw error
    }
  }

  private handleTokenExpired() {
    // 只有"确实带着 token 却被服务端拒绝"才算真的失效。
    // 若这次请求压根没带 token（例如组件在 rehydrate 恢复登录态之前就发了请求），
    // 服务端同样返回 41003 —— 此时清空登录态属于误伤，会把已登录用户踢下线。
    if (!this.token && !useUserStore.getState().token) return
    useUserStore.getState().logout()
    import('@/components/shared').then(({ showInfo }) => {
      showInfo(i18n.t('auth.sessionExpired'))
    }).catch(() => {})
  }

  private async handlePlainResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      if (AUTH_ERROR_CODES.has(error.code)) {
        this.handleTokenExpired()
      }
      throw {
        message: getErrorMessage(error.code, error.msg),
        status: response.status,
        code: error.code,
      } as ApiError
    }

    if (response.status === 204) {
      return {} as T
    }

    const parsed = await response.json()

    // HTTP 200 但业务层返回认证错误码，强制登出
    if (parsed.code && AUTH_ERROR_CODES.has(parsed.code)) {
      this.handleTokenExpired()
      throw {
        message: getErrorMessage(parsed.code, parsed.msg),
        status: 401,
        code: parsed.code,
      } as ApiError
    }

    return parsed as T
  }

  private async handleCipherResponse<T>(response: Response): Promise<T> {
    const buffer = await response.arrayBuffer()
    const encrypted = new Uint8Array(buffer)

    const bodyMD = response.headers.get('X-Body-MD') || ''
    const result = checkAndGetOriginResp(encrypted, bodyMD)

    if (result.error) {
      throw {
        message: `${i18n.t('error.decryptFailed')}: ${result.error}`,
        status: response.status,
      } as ApiError
    }

    const decoder = new TextDecoder()
    const jsonStr = decoder.decode(result.response!)
    const parsed = JSON.parse(jsonStr)

    // 拦截认证相关错误码，强制登出
    if (parsed.code && AUTH_ERROR_CODES.has(parsed.code)) {
      this.handleTokenExpired()
      throw {
        message: getErrorMessage(parsed.code, parsed.msg),
        status: 401,
        code: parsed.code,
      } as ApiError
    }

    return parsed as T
  }
}

export const api = new ApiClient()
export { ApiClient }
